import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Tab, Tabs } from 'react-bootstrap';
import { FaGamepad } from 'react-icons/fa';
import siteService from '../services/siteService';

const BatchImportModal = ({ show, onHide, onSuccess }) => {
  const [importText, setImportText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('text');
  
  // 游戏站点模板
  const gameSiteTemplates = [
    {
      name: '流行游戏站点集合',
      description: '添加常见的游戏资讯和发行网站',
      sites: [
        {
          name: 'Steam 新游戏',
          url: 'https://store.steampowered.com',
          sitemapUrl: 'https://store.steampowered.com/sitemap_games_1.xml',
          checkFrequency: 'daily'
        },
        {
          name: 'IGN游戏',
          url: 'https://www.ign.com',
          sitemapUrl: 'https://www.ign.com/sitemaps/articles/index.xml',
          checkFrequency: 'daily'
        },
        {
          name: '3DM游戏网',
          url: 'https://www.3dmgame.com',
          sitemapUrl: 'https://www.3dmgame.com/sitemap.xml',
          checkFrequency: 'daily'
        },
        {
          name: 'GameSpot',
          url: 'https://www.gamespot.com',
          sitemapUrl: 'https://www.gamespot.com/sitemaps/article/index.xml',
          checkFrequency: 'daily'
        }
      ]
    },
    {
      name: '国内游戏站点',
      description: '添加中国游戏新闻和发行信息站点',
      sites: [
        {
          name: '游民星空',
          url: 'https://www.gamersky.com',
          sitemapUrl: 'https://www.gamersky.com/sitemap.xml',
          checkFrequency: 'daily'
        },
        {
          name: '机核网',
          url: 'https://www.gcores.com',
          sitemapUrl: 'https://www.gcores.com/sitemap.xml',
          checkFrequency: 'daily'
        },
        {
          name: '游侠网',
          url: 'https://www.ali213.net',
          sitemapUrl: 'https://www.ali213.net/sitemap.xml',
          checkFrequency: 'daily'
        }
      ]
    },
    {
      name: '游戏发行商',
      description: '监控主要游戏发行商的官方网站',
      sites: [
        {
          name: 'PlayStation 博客',
          url: 'https://blog.playstation.com',
          sitemapUrl: 'https://blog.playstation.com/sitemap_index.xml',
          checkFrequency: 'daily'
        },
        {
          name: 'Xbox Wire',
          url: 'https://news.xbox.com',
          sitemapUrl: 'https://news.xbox.com/sitemap_index.xml',
          checkFrequency: 'daily'
        },
        {
          name: 'Nintendo',
          url: 'https://www.nintendo.com',
          sitemapUrl: 'https://www.nintendo.com/sitemap.xml',
          checkFrequency: 'daily'
        }
      ]
    }
  ];

  // 处理导入
  const handleImport = async () => {
    try {
      setLoading(true);
      setError('');

      if (activeTab === 'text') {
        // 文本导入处理
        if (!importText.trim()) {
          setError('请输入要导入的站点数据');
          setLoading(false);
          return;
        }

        // 解析文本
        const sites = parseImportText(importText);
        
        if (sites.length === 0) {
          setError('无法解析输入的站点数据，请检查格式');
          setLoading(false);
          return;
        }

        // 调用API批量导入
        const result = await siteService.batchImportSites(sites);
        
        // 成功处理
        onSuccess(result);
        onHide();
      } else if (activeTab === 'templates') {
        // 模板导入处理
        const selectedTemplate = gameSiteTemplates.find(
          t => t.name === activeTemplateId
        );
        
        if (!selectedTemplate) {
          setError('请选择一个游戏站点模板');
          setLoading(false);
          return;
        }
        
        // 调用API批量添加游戏站点
        const result = await siteService.addGameSites(selectedTemplate.sites);
        
        // 成功处理
        onSuccess(result);
        onHide();
      }
    } catch (err) {
      setError('导入失败: ' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 解析导入文本
  const parseImportText = (text) => {
    const sites = [];
    
    // 尝试解析为JSON
    try {
      const jsonData = JSON.parse(text);
      
      // 如果是数组，直接使用
      if (Array.isArray(jsonData)) {
        return jsonData.filter(site => site.name && site.url);
      }
      
      // 如果是对象，可能是单个站点
      if (jsonData.name && jsonData.url) {
        return [jsonData];
      }
    } catch (e) {
      // 不是有效的JSON，尝试其他格式
    }
    
    // 尝试解析为CSV/TSV格式
    const lines = text.split('\n');
    
    for (const line of lines) {
      const parts = line.split(/[,\t]/);
      
      if (parts.length >= 2) {
        const name = parts[0].trim();
        const url = parts[1].trim();
        
        if (name && url) {
          const site = {
            name,
            url,
            checkFrequency: 'daily'
          };
          
          // 如果有第三列，作为sitemapUrl
          if (parts.length >= 3 && parts[2].trim()) {
            site.sitemapUrl = parts[2].trim();
            site.checkSitemap = true;
          }
          
          sites.push(site);
        }
      }
    }
    
    return sites;
  };
  
  // 处理模板选择
  const [activeTemplateId, setActiveTemplateId] = useState('');
  
  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>批量导入监控站点</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          <Tab eventKey="templates" title={<><FaGamepad className="me-1" /> 游戏站点模板</>}>
            <p className="text-muted mb-3">
              选择预设的游戏站点模板，一键添加多个游戏站点进行监控
            </p>
            
            {gameSiteTemplates.map((template) => (
              <div 
                key={template.name}
                className={`template-card p-3 mb-3 border rounded ${
                  activeTemplateId === template.name ? 'border-primary bg-light' : ''
                }`}
                onClick={() => setActiveTemplateId(template.name)}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex align-items-center">
                  <div className="me-3">
                    <FaGamepad size={24} className="text-primary" />
                  </div>
                  <div>
                    <h5 className="mb-1">{template.name}</h5>
                    <p className="mb-1 text-muted">{template.description}</p>
                    <small className="text-muted">
                      包含 {template.sites.length} 个游戏站点
                    </small>
                  </div>
                </div>
              </div>
            ))}
            
            {activeTemplateId && (
              <div className="mt-3">
                <h6>包含的站点:</h6>
                <ul className="list-group">
                  {gameSiteTemplates
                    .find(t => t.name === activeTemplateId)
                    ?.sites.map((site, index) => (
                      <li key={index} className="list-group-item">
                        <div className="fw-bold">{site.name}</div>
                        <div className="small text-muted">{site.url}</div>
                        {site.sitemapUrl && (
                          <div className="small text-muted">
                            Sitemap: {site.sitemapUrl}
                          </div>
                        )}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </Tab>
          
          <Tab eventKey="text" title="文本导入">
            <p className="text-muted mb-3">
              支持多种格式导入:
            </p>
            <ul className="text-muted mb-3">
              <li>JSON数组格式</li>
              <li>CSV格式 (名称,URL,Sitemap URL)</li>
              <li>每行一个站点，以逗号或制表符分隔</li>
            </ul>
            
            <Form.Group className="mb-3">
              <Form.Label>输入要导入的站点数据:</Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={
                  '示例格式:\n' +
                  'Site1,https://example1.com,https://example1.com/sitemap.xml\n' +
                  'Site2,https://example2.com\n\n' +
                  '或者JSON格式:\n' +
                  '[{"name":"Site1","url":"https://example1.com"},\n' +
                  '{"name":"Site2","url":"https://example2.com"}]'
                }
              />
            </Form.Group>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          取消
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={loading}>
          {loading ? '导入中...' : '导入站点'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BatchImportModal; 